/**
 * React hook wrapping ChangeQueue for element mutation batching.
 *
 * Provides `enqueueAdd`, `enqueueUpdate`, `enqueueDelete` that apply
 * optimistically in the UI while the queue handles debounced API delivery.
 *
 * All payloads conform to the StowedElement contract.
 */

import { useCallback, useEffect, useRef } from 'react'
import {
  ChangeQueue,
  type ChangeQueueOptions,
  type FlushOptions,
  type UpdateOperation,
} from '@/api/queues/change-queue'
import type { ElementLayer, LinkedTextElement, StowedElement } from '@/api/types/stowed-element'

type QueueHistoryMeta = {
  historyTxnId?: string
  historyActionType?: string
  historyDirection?: 'undo' | 'redo'
  batchElementIds?: string[]
}

export interface UseChangeQueueOptions extends ChangeQueueOptions {
  /** Default spread id when not supplied per-call. */
  spreadId: string
  /** Default layer when not supplied per-call. */
  layer?: ElementLayer
}

export function useChangeQueue({
  spreadId,
  layer = 'userLayer',
  ...queueOpts
}: UseChangeQueueOptions) {
  const queueRef = useRef<ChangeQueue | null>(null)

  useEffect(() => {
    queueRef.current = new ChangeQueue(queueOpts)
    return () => {
      void queueRef.current?.flush()
      queueRef.current?.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadId])

  const enqueueAdd = useCallback(
    (
      payload: StowedElement,
      overrides?: {
        layer?: ElementLayer
        meta?: QueueHistoryMeta
        isPenElement?: boolean
        linkedTextElements?: LinkedTextElement
      }
    ) =>
      queueRef.current?.enqueue({
        kind: 'add',
        objectId: payload.objectId,
        spreadId,
        layer: overrides?.layer ?? layer,
        payload,
        meta: overrides?.meta,
        isPenElement: overrides?.isPenElement,
        linkedTextElements: overrides?.linkedTextElements,
      }),
    [spreadId, layer]
  )

  const enqueueUpdate = useCallback(
    (
      payload: StowedElement,
      overrides?: {
        layer?: ElementLayer
        operation?: UpdateOperation
        meta?: QueueHistoryMeta
        linkedTextElements?: LinkedTextElement
      }
    ) =>
      queueRef.current?.enqueue({
        kind: 'update',
        updateOperation: overrides?.operation ?? 'update',
        objectId: payload.objectId,
        spreadId,
        layer: overrides?.layer ?? layer,
        payload,
        meta: overrides?.meta,
        linkedTextElements: overrides?.linkedTextElements,
      }),
    [spreadId, layer]
  )

  const enqueueDelete = useCallback(
    (
      objectId: string,
      overrides?: {
        layer?: ElementLayer
        meta?: QueueHistoryMeta
      }
    ) =>
      queueRef.current?.enqueue({
        kind: 'delete',
        objectId,
        spreadId,
        layer: overrides?.layer ?? layer,
        meta: overrides?.meta,
      }),
    [spreadId, layer]
  )

  const flush = useCallback(
    (opts?: FlushOptions) => queueRef.current?.flush(opts) ?? Promise.resolve(),
    []
  )

  return { enqueueAdd, enqueueUpdate, enqueueDelete, flush }
}
